import React, { useState, useEffect } from 'react';
import { conferencesAPI } from '../services/api';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Globe, MapPin, Calendar, Award, GraduationCap, ArrowRight, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const InternationalSpeaking = () => {
  const [international, setInternational] = useState([]);
  const [national, setNational] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await conferencesAPI.getAll();
        setInternational(data.filter(c => c.type === 'international'));
        setNational(data.filter(c => c.type === 'national'));
      } catch {
        setError('Failed to load conferences');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <section id="speaking" className="py-20 px-4 bg-gradient-to-b from-white via-teal-50/30 to-white" data-testid="speaking-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-full text-sm font-semibold mb-4 shadow-lg">
            <GraduationCap className="w-5 h-5" />
            <span>Global Recognition & Academic Excellence</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            International Speaking &<br />
            <span className="text-teal-700">Faculty Positions</span>
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Keynote Speaker, Guest Faculty, and Fellow at premier institutions across the globe,
            bringing cutting-edge oncology expertise from international centers to Northeast India
          </p>
        </div>

        <Tabs defaultValue="international" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12 h-14">
            <TabsTrigger value="international" className="text-base font-semibold data-[state=active]:bg-teal-700 data-[state=active]:text-white">
              <Globe className="w-5 h-5 mr-2" />
              International
            </TabsTrigger>
            <TabsTrigger value="national" className="text-base font-semibold data-[state=active]:bg-teal-700 data-[state=active]:text-white">
              <Award className="w-5 h-5 mr-2" />
              National
            </TabsTrigger>
          </TabsList>

          <TabsContent value="international">
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading...</div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                  {international.map((conf) => (
                    <Card key={conf.id} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-teal-100 bg-white" data-testid={`intl-conf-${conf.id}`}>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-4">
                          {conf.country && (
                            <Badge className="bg-teal-700 text-white text-xs">
                              <Globe className="w-3 h-3 mr-1" />{conf.country}
                            </Badge>
                          )}
                          {conf.role && (
                            <Badge variant="outline" className="text-xs border-teal-200 text-teal-700">{conf.role}</Badge>
                          )}
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-3 leading-tight">{conf.name}</h3>
                        <div className="space-y-1.5 mb-4">
                          <div className="flex items-center gap-2 text-gray-600 text-sm">
                            <MapPin className="w-3.5 h-3.5 text-teal-600 flex-shrink-0" />
                            <span>{conf.location}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600 text-sm">
                            <Calendar className="w-3.5 h-3.5 text-teal-600 flex-shrink-0" />
                            <span>{conf.date}</span>
                          </div>
                        </div>
                        <p className="text-gray-600 text-sm leading-relaxed mb-4">{conf.description}</p>
                        {conf.blog_slug && (
                          <Link to={`/blog/${conf.blog_slug}`} className="inline-flex items-center gap-1.5 text-teal-700 hover:text-teal-600 font-semibold text-sm transition-colors" data-testid={`conf-blog-link-${conf.id}`}>
                            View Photos & Details <ArrowRight className="w-4 h-4" />
                          </Link>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="bg-gradient-to-r from-teal-700 via-teal-600 to-teal-700 rounded-2xl p-8 md:p-12 text-white shadow-2xl">
                  <h3 className="text-2xl md:text-3xl font-bold mb-8 text-center">International Recognition</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                    <div className="transform hover:scale-105 transition-transform">
                      <p className="text-4xl md:text-5xl font-bold mb-2">9</p>
                      <p className="text-sm opacity-90 font-medium">Countries Visited</p>
                    </div>
                    <div className="transform hover:scale-105 transition-transform">
                      <p className="text-4xl md:text-5xl font-bold mb-2">15+</p>
                      <p className="text-sm opacity-90 font-medium">International Programs</p>
                    </div>
                    <div className="transform hover:scale-105 transition-transform">
                      <p className="text-4xl md:text-5xl font-bold mb-2">10+</p>
                      <p className="text-sm opacity-90 font-medium">Travel Grants</p>
                    </div>
                    <div className="transform hover:scale-105 transition-transform">
                      <p className="text-4xl md:text-5xl font-bold mb-2">100+</p>
                      <p className="text-sm opacity-90 font-medium">Conferences Attended</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="national">
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading...</div>
            ) : national.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-2">National conferences will be added here.</p>
                <p className="text-gray-400 text-sm">You can add them from the Admin Panel.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {national.map((conf) => (
                  <Card key={conf.id} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 bg-white" data-testid={`natl-conf-${conf.id}`}>
                    <CardContent className="p-6">
                      {conf.role && (
                        <Badge variant="outline" className="text-xs border-teal-200 text-teal-700 mb-3">{conf.role}</Badge>
                      )}
                      <h3 className="text-lg font-bold text-gray-900 mb-3">{conf.name}</h3>
                      <div className="space-y-1.5 mb-4">
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <MapPin className="w-3.5 h-3.5 text-teal-600" />
                          <span>{conf.location}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <Calendar className="w-3.5 h-3.5 text-teal-600" />
                          <span>{conf.date}</span>
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed mb-4">{conf.description}</p>
                      {conf.blog_slug && (
                        <Link to={`/blog/${conf.blog_slug}`} className="inline-flex items-center gap-1.5 text-teal-700 hover:text-teal-600 font-semibold text-sm transition-colors">
                          View Photos & Details <ArrowRight className="w-4 h-4" />
                        </Link>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default InternationalSpeaking;
